import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  EMPTY_TABLE_DRAFT,
  MAX_TABLE_CAPACITY,
  TABLE_PRESETS,
  applySquareSymmetry,
  draftCapacity,
  parseSeatInput,
  tableRequests,
  validateTableDraft,
} from '../../../lib/seatingTables';
import TableCanvas from './TableCanvas';
import type { TableDraft } from '../../../lib/seatingTables';
import type { CreateSeatingTableRequest, SeatingTableShape } from '../../../types/seating';

/**
 * Add a table: pick the shape, say how many chairs, see it before you commit.
 *
 * Seats per side is the primitive, not the shape — which is what lets one form
 * express a 10-top round, an 8-top square and a 24-seat banquet table without a
 * new control for each. The presets exist because that generality is only useful
 * if a host never has to discover it: "long table, 24" is one tap, and the four
 * side boxes underneath then show what it meant.
 */
export default function AddTableDialog({
  existingLabels,
  seatPitchCm,
  saving,
  error,
  onClose,
  onAdd,
}: {
  existingLabels: string[];
  seatPitchCm: number;
  saving: boolean;
  error: string;
  onClose: () => void;
  onAdd: (tables: CreateSeatingTableRequest[]) => void;
}) {
  const [draft, setDraft] = useState<TableDraft>(() => ({
    ...EMPTY_TABLE_DRAFT,
    label: `Table ${existingLabels.length + 1}`,
  }));

  const update = (patch: Partial<TableDraft>) =>
    setDraft((prev) => applySquareSymmetry({ ...prev, ...patch }));

  const capacity = draftCapacity(draft);
  const problem = validateTableDraft(draft);
  const requests = useMemo(
    () => (problem ? [] : tableRequests(draft, existingLabels)),
    [draft, existingLabels, problem],
  );

  /**
   * A seat count, spelled as text on purpose.
   *
   * `type="number"` cannot be corrected once it is wrong: React only rewrites a
   * number input when `node.valueAsNumber` differs from the value it is
   * rendering, and "020" already reads back as 20. The state was right and the
   * box still said 020 — because the field starts at a digit, every number a
   * host types lands in front of one. As text the comparison is string-to-string,
   * so stripping the leading zero here actually reaches the screen.
   */
  const numberField = (
    label: string,
    value: number,
    patch: (next: number) => Partial<TableDraft>,
    helper?: string,
  ) => (
    <TextField
      label={label}
      type="text"
      size="small"
      value={value}
      helperText={helper}
      onChange={(event) => update(patch(parseSeatInput(event.target.value)))}
      inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', 'aria-label': label }}
      sx={{ width: '100%' }}
    />
  );

  /**
   * Wrap-aware spacing. `Stack spacing` is a margin on every child but the
   * first, which survives wrapping — the first box on each new line keeps it and
   * the column edges stop lining up. A grid gutters between tracks instead, and
   * hands narrow screens two columns rather than a four-storey stack that pushes
   * the buttons under the keyboard.
   */
  const sideGrid = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(116px, 1fr))',
    gap: 2,
  } as const;

  // A phone keyboard eats a centred dialog's buttons. Full screen keeps the
  // form scrollable and Add reachable with the numeric pad up.
  const fullScreen = useMediaQuery(useTheme().breakpoints.down('sm'));

  return (
    <Dialog
      open
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={fullScreen}
    >
      <DialogTitle sx={{ pb: 0.5 }}>Add a table</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}

          <Box>
            <Typography variant="caption" color="text.secondary">Start from a common table</Typography>
            <Stack direction="row" sx={{ mt: 0.5, flexWrap: 'wrap', gap: 1 }}>
              {TABLE_PRESETS.map((preset) => (
                <Chip
                  key={preset.id}
                  label={preset.label}
                  title={preset.hint}
                  variant="outlined"
                  onClick={() => update(preset.draft)}
                />
              ))}
            </Stack>
          </Box>

          <ToggleButtonGroup
            exclusive
            size="small"
            value={draft.shape}
            onChange={(_, shape: SeatingTableShape | null) => {
              if (shape) update({ shape });
            }}
          >
            <ToggleButton value="round">Round</ToggleButton>
            <ToggleButton value="square">Square</ToggleButton>
            <ToggleButton value="rectangle">Rectangle / long table</ToggleButton>
          </ToggleButtonGroup>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              fullWidth
              size="small"
              label={draft.quantity > 1 ? 'Name these tables' : 'Table name'}
              value={draft.label}
              helperText={draft.quantity > 1 ? 'Numbered automatically — “Table 1”, “Table 2”…' : undefined}
              onChange={(event) => update({ label: event.target.value.slice(0, 120) })}
            />
            <Box sx={{ width: { xs: '100%', sm: 160 }, flexShrink: 0 }}>
              {numberField('How many', draft.quantity, (quantity) => ({ quantity }), 'Added in one go')}
            </Box>
          </Stack>

          {draft.shape === 'round' ? (
            <Box sx={{ width: { xs: '100%', sm: 160 } }}>
              {numberField('Chairs', draft.seatCount, (seatCount) => ({ seatCount }))}
            </Box>
          ) : draft.shape === 'square' ? (
            <Box sx={sideGrid}>
              {numberField('Top and bottom (each)', draft.seatsTop, (seatsTop) => ({ seatsTop }))}
              {numberField('Left and right (each)', draft.seatsLeft, (seatsLeft) => ({ seatsLeft }))}
            </Box>
          ) : (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Chairs per side. A long banquet table is 11 and 11 down the sides with one chair at
                each head — 24 seats, 7.2 metres of table.
              </Typography>
              <Box sx={sideGrid}>
                {numberField('Top', draft.seatsTop, (seatsTop) => ({ seatsTop }))}
                {numberField('Bottom', draft.seatsBottom, (seatsBottom) => ({ seatsBottom }))}
                {numberField('Left head', draft.seatsLeft, (seatsLeft) => ({ seatsLeft }))}
                {numberField('Right head', draft.seatsRight, (seatsRight) => ({ seatsRight }))}
              </Box>
            </Box>
          )}

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">Preview</Typography>
              <Chip size="small" label={`${capacity} seat${capacity === 1 ? '' : 's'}`} />
            </Stack>
            {capacity > 0 && capacity <= MAX_TABLE_CAPACITY ? (
              <TableCanvas
                table={draft}
                seatPitchCm={seatPitchCm}
                ariaLabel={`Preview of ${draft.label || 'this table'}`}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                {capacity > MAX_TABLE_CAPACITY
                  ? 'Too many chairs to draw — bring it under 60 and the table comes back.'
                  : 'Add a chair to see the table.'}
              </Typography>
            )}
          </Paper>

          {problem ? <Alert severity="warning">{problem}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          disabled={saving || !!problem}
          onClick={() => onAdd(requests)}
        >
          {saving
            ? 'Adding…'
            : draft.quantity > 1
              ? `Add ${draft.quantity} tables`
              : 'Add table'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
